/* A Leading Role - name the saving in the cart drawer.
 *
 * alr-pair.js writes one line into the drawer:
 *
 *     Bundle savings   -$5.00
 *
 * built from cart.total_discount. That was right when the pair/bundle rules
 * were the only automatic discounts on the store. They are not any more:
 * Front Row points redeem through a Shopify Function, which is also an
 * automatic discount, so a member spending points is told they have a bundle
 * saving on a single item with nothing to bundle it with.
 *
 * This splits that one line by where the money actually came from:
 *
 *     Bundle savings     -$12.00     (only the pair / cooking / add-on rules)
 *     Front Row points   -$5.00
 *
 * Additive on purpose: assets/alr-pair.js owns the bundling work and is edited
 * by it, so nothing there is touched. The proper long-term home for this is
 * that file's savings block.
 *
 * No flash of the wrong label: MutationObserver callbacks run before paint, so
 * the row is renamed to the neutral "Savings" the moment it appears and is
 * split as soon as /cart.js answers. Every state it passes through is true.
 */
(function () {
  'use strict';

  var MARK = 'data-fr-savings';
  var cart = null;
  var seq = 0;

  function money(cents) {
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }

  // "Front Row - points" (the discount's title) and "Front Row points - $5.00
  // off" (the function's own message) must both count; punctuation between the
  // words is not something to depend on.
  function isPoints(text) {
    return /front row points/.test(
      String(text == null ? '' : text).toLowerCase().replace(/[^a-z0-9]+/g, ' ')
    );
  }

  function pointsCents(c) {
    var cap = Number(c.total_discount || 0);
    var capped = function (n) { return n > cap ? cap : (n > 0 ? n : 0); };

    // The cart reports the same discount in more than one place. The
    // cart-level application is the whole figure, so when it is there it is
    // the answer and the line entries are the same money split up - adding
    // both counts it twice.
    var fromCart = 0;
    (c.cart_level_discount_applications || []).forEach(function (d) {
      if (isPoints(d.title) || isPoints(d.description)) {
        fromCart += Number(d.total_allocated_amount || 0);
      }
    });
    if (fromCart > 0) return capped(fromCart);

    // No cart-level entry, so add up the lines instead. Newer carts carry
    // line_level_discount_allocations and older ones only .discounts, and a
    // line with no allocations at all falls back to .discounts on its own -
    // which is exactly where an un-bundled line hides its share of the points.
    var fromLines = 0;
    (c.items || []).forEach(function (it) {
      var allocs = it.line_level_discount_allocations;
      if (allocs && allocs.length) {
        allocs.forEach(function (a) {
          var app = a.discount_application || {};
          if (isPoints(app.title) || isPoints(app.description)) fromLines += Number(a.amount || 0);
        });
      } else {
        (it.discounts || []).forEach(function (d) {
          if (isPoints(d.title)) fromLines += Number(d.amount || 0);
        });
      }
    });
    return capped(fromLines);
  }

  function label(row, text, cents) {
    var t = row.querySelector('.t');
    var v = row.querySelector('.v');
    if (t) t.textContent = text;
    if (v) v.innerHTML = '&minus;' + money(cents);
  }

  function split(row, my) {
    if (!cart || my !== seq || !row.isConnected) return;

    var all = Number(cart.total_discount || 0);
    var pts = pointsCents(cart);
    var rest = all - pts;

    if (pts <= 0) {
      // Genuinely a bundle. Put back the wording alr-pair.js meant.
      label(row, 'Bundle savings', all);
      return;
    }

    if (rest <= 0) {
      label(row, 'Front Row points', pts);
      return;
    }

    label(row, 'Bundle savings', rest);
    if (row.parentNode.querySelector('[' + MARK + '="points"]')) return;

    var extra = row.cloneNode(true);
    extra.setAttribute(MARK, 'points');
    label(extra, 'Front Row points', pts);
    row.parentNode.insertBefore(extra, row.nextSibling);
  }

  // Every drawer re-render bumps seq, so an answer for a cart that has since
  // changed is dropped rather than written over the new one.
  function load(my) {
    return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) { if (my === seq) cart = c; return c; })
      .catch(function () { return null; });
  }

  function claim(root) {
    var rows = root.querySelectorAll('.cart-savings:not([' + MARK + '])');
    if (!rows.length) return;

    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      row.setAttribute(MARK, 'own');
      // Synchronous, before the browser paints: the shopper never reads
      // "Bundle savings" on a cart that has no bundle in it.
      var name = row.querySelector('.t');
      if (name) name.textContent = 'Savings';
      (function (r, my) {
        if (cart) split(r, my);
        else load(my).then(function () { split(r, my); });
      })(row, seq);
    }
  }

  function watch() {
    var root = document.getElementById('mbag-root');
    if (!root) return;

    new MutationObserver(function () {
      // alr.js rebuilds the drawer wholesale on every cart change, so the
      // numbers we hold are stale the moment it does.
      if (root.querySelector('.cart-savings:not([' + MARK + '])')) {
        cart = null;
        seq++;
      }
      claim(root);
    }).observe(root, { childList: true, subtree: true });

    claim(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
})();
